import { memo, useCallback, useContext } from "react";
import PropTypes from "prop-types";
import BackgroundProgressiveImg from "app/pages/.shared/BackgroundProgressiveImg";
import "./LookAlikeProduct.scss";
import IconRight from "app/pages/.shared/static/icons/IconRight";
import Button from "app/pages/.shared/form/Button";
import { Link } from "react-router-dom";
import { RESOLUTION } from "app/pages/.shared/responsive/responsiveReducer";
import ProductCardFromPrice from "../ProductCard/ProductCardFromPrice";
import { FormattedMessage } from "react-intl";
import ProductRating from "app/pages/FicheProduit/components/ProductRating/ProductRating";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import ProductTopBar from "app/pages/Listing/Product/ProductTopBar/ProductTopBar";
import FavoriteBadge from "app/pages/.shared/Badge/FavoriteBadge";
import classNames from "classnames";
import AppGlobalsContext from "app/AppGlobalsContext";
import { RATING_TYPE } from "app/constants";

const IMG_SIZES_DESKTOP = {
	large: { width: 502, height: 282 },
	xlarge: { width: 502, height: 282 },
	medium: { width: 502, height: 282 },
	small: { width: 502, height: 282 },
};

const LookAlikeProduct = ({
	onClick = () => {},
	uri = "",
	image = { src: "", thumbnail: "" },
	title = "",
	subtitle = "",
	tripAdvisor = { id: "", rating: 0, reviewsCount: 0, awardLabel: "" },
	startingPrice = 0,
	price = 0,
	isRental,
	offerPriceType = "",
	nbOfNights = 0,
	includeFlightOption = false,
	includeTrainOption = false,
	showTransportModeAndNightCount = true,
	flashSaleDeadline = { hours: 0, days: 0 },
	isFavorite = false,
}) => {
	const { resolution } = useContext(AppGlobalsContext);

	const hotelName = subtitle || title;
	const isFlashSale = flashSaleDeadline.hours > 0 || flashSaleDeadline.days > 0;
	const shouldOpenInNewTab = resolution === RESOLUTION.LARGE || resolution === RESOLUTION.XLARGE;

	const handleProductClick = useCallback(() => {
		onClick();
	}, [onClick]);

	const cardContentClassName = classNames("lookalike-product__content", {
		"lookalike-product__content--no-subtitle": subtitle.length === 0,
	});

	return (
		<Link
			className="lookalike-product"
			to={{ pathname: `/${uri}` }}
			rel="noopener noreferrer"
			target={shouldOpenInNewTab ? "_blank" : ""}
			onClick={handleProductClick}
		>
			<article className="lookalike-product__card">
				<figure className="lookalike-product__photo">
					<BackgroundProgressiveImg
						sizes={IMG_SIZES_DESKTOP}
						cloudinaryOptions={["c_fill"]}
						alt={hotelName}
						src={image.src}
						thumbnail={image.thumbnail}
						backgroundPosition="center"
					/>

					{isFlashSale && (
						<ProductTopBar
							isFlashsale={true}
							productExpirationHours={flashSaleDeadline.hours}
							productExpirationDays={flashSaleDeadline.days}
							hideSuffix={true}
						/>
					)}

					{isFavorite && <FavoriteBadge showLabel={false} />}
				</figure>

				<div className={cardContentClassName}>
					<header className="lookalike-product__destination">
						<Typography
							variant={TYPOGRAPHY_VARIANTS.LARGE}
							variantMD={TYPOGRAPHY_VARIANTS.XL}
							isBold
							component="p"
						>
							{title}
						</Typography>

						<Typography variant={TYPOGRAPHY_VARIANTS.SMALL} isBold component="p">
							{subtitle}
						</Typography>

						<ProductRating
							ratingType={RATING_TYPE.TRIP_ADVISOR}
							locationName={hotelName}
							tripAdvisor={tripAdvisor}
						/>
					</header>

					<footer className="lookalike-product__footer">
						<ProductCardFromPrice
							pricePrefix={
								startingPrice > 0 ? (
									<FormattedMessage id="general.from.label" />
								) : (
									undefined
								)
							}
							priceSuffix={
								isRental ? (
									<FormattedMessage id="general.amount.per.accommodation.suffix.label.short" />
								) : (
									<FormattedMessage id="general.amount.per.person.suffix.label.short" />
								)
							}
							travelByTrainAvailable={includeTrainOption}
							value={startingPrice || price}
							includeOfferWithFlight={includeFlightOption}
							offerPriceType={offerPriceType}
							duration={nbOfNights}
							showFromInfo={showTransportModeAndNightCount}
						/>

						{subtitle.length > 0 && (
							<Button variant="primary" className="lookalike-product__button">
								<IconRight width={16} height={16} />
							</Button>
						)}
					</footer>
				</div>
			</article>
		</Link>
	);
};

LookAlikeProduct.propTypes = {
	onClick: PropTypes.func,
	uri: PropTypes.string,
	image: PropTypes.shape({
		src: PropTypes.string,
		thumbnail: PropTypes.string,
	}),
	title: PropTypes.string,
	subtitle: PropTypes.string,
	tripAdvisor: PropTypes.shape({
		id: PropTypes.string,
		rating: PropTypes.number,
		reviewsCount: PropTypes.number,
		awardLabel: PropTypes.string,
	}),
	startingPrice: PropTypes.number,
	price: PropTypes.number,
	offerPriceType: PropTypes.string,
	nbOfNights: PropTypes.number,
	includeFlightOption: PropTypes.bool,
	includeTrainOption: PropTypes.bool,
	isRental: PropTypes.bool,
	showTransportModeAndNightCount: PropTypes.bool,
	flashSaleDeadline: PropTypes.shape({
		hours: PropTypes.number,
		days: PropTypes.number,
	}),
	isFavorite: PropTypes.bool,
};

export default memo(LookAlikeProduct);
